import { useCallback } from 'react'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import Select, { SelectChangeEvent } from '@mui/material/Select'
import Stack from '@mui/material/Stack'
import { DateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker'
import { SingleInputDateRangeField } from '@mui/x-date-pickers-pro/SingleInputDateRangeField'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import type { DateRange } from '@mui/x-date-pickers-pro'
import type { Dayjs } from 'dayjs'
import dayjs from 'dayjs'

import { useAppSelector } from 'src/store/hooks'
import { useAutoFetch } from 'src/v3/hooks/use-auto-fetch'
import {
  fetchLinkedServices,
  selectLinkedServices,
  selectLinkedServicesStatus,
} from '../../manage.slice'
import type {
  ManageEventsFilters,
  ManageEventsIndexTab,
} from '../manage-events.slice'
import { selectManageEventCategories } from '../manage-events.slice'

let STATUS_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'registration-open', label: 'Registration open' },
  { value: 'registration-closed', label: 'Registration closed' },
]

type Props = {
  filters: ManageEventsFilters
  // Unscheduled events have no next date and a single status, so those filters are hidden there.
  tab: ManageEventsIndexTab
  onChange: (partial: Partial<ManageEventsFilters>) => void
}

let EventsFilters = ({ filters, tab, onChange }: Props) => {
  let linkedServices = useAppSelector(selectLinkedServices)
  let linkedServicesStatus = useAppSelector(selectLinkedServicesStatus)
  let categories = useAppSelector(selectManageEventCategories)

  useAutoFetch(linkedServicesStatus, fetchLinkedServices)

  let handleService = useCallback(
    (e: SelectChangeEvent) => onChange({ linkedServiceID: e.target.value }),
    [onChange],
  )

  let handleCategory = useCallback(
    (e: SelectChangeEvent) => onChange({ category: e.target.value }),
    [onChange],
  )

  let handleStatus = useCallback(
    (e: SelectChangeEvent) =>
      onChange({
        registrationStatus: e.target
          .value as ManageEventsFilters['registrationStatus'],
      }),
    [onChange],
  )

  let handleDateRange = useCallback(
    ([from, to]: DateRange<Dayjs>) => {
      onChange({
        dateFrom: from ? from.format('YYYY-MM-DD') : null,
        dateTo: to ? to.format('YYYY-MM-DD') : null,
      })
    },
    [onChange],
  )

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Stack direction="row" className="flex-wrap gap-4 items-center">
        <FormControl
          sx={{
            flex: 1,
            minWidth: 160,
            backgroundColor: 'background.paperDark',
          }}
        >
          <InputLabel id="events-service-label" shrink>
            Linked service
          </InputLabel>
          <Select
            labelId="events-service-label"
            label="Linked service"
            value={filters.linkedServiceID}
            onChange={handleService}
            displayEmpty
          >
            <MenuItem value="">
              <em>All Linked Services</em>
            </MenuItem>
            {linkedServices.map((s) => (
              <MenuItem key={s.id} value={s.id}>
                {s.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl
          sx={{
            flex: 1,
            minWidth: 160,
            backgroundColor: 'background.paperDark',
          }}
        >
          <InputLabel id="events-category-label" shrink>
            Category
          </InputLabel>
          <Select
            labelId="events-category-label"
            label="Category"
            value={filters.category}
            onChange={handleCategory}
            displayEmpty
          >
            <MenuItem value="">
              <em>All Categories</em>
            </MenuItem>
            {[...categories.standard, ...categories.custom].map((c) => (
              <MenuItem key={c} value={c}>
                {c.replaceAll('|', '/')}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {tab === 'scheduled' && (
          <>
            <DateRangePicker
              value={[
                filters.dateFrom ? dayjs(filters.dateFrom) : null,
                filters.dateTo ? dayjs(filters.dateTo) : null,
              ]}
              onChange={handleDateRange}
              label="Date range"
              slots={{ field: SingleInputDateRangeField }}
              slotProps={{
                field: {
                  sx: {
                    flex: 1,
                    minWidth: 160,
                    '& .MuiPickersInputBase-root': {
                      backgroundColor: 'background.paperDark',
                    },
                  },
                } as any,
              }}
            />
            <FormControl
              sx={{
                flex: 1,
                minWidth: 160,
                backgroundColor: 'background.paperDark',
              }}
            >
              <InputLabel id="events-status-label" shrink>
                Status
              </InputLabel>
              <Select
                labelId="events-status-label"
                label="Status"
                value={filters.registrationStatus}
                onChange={handleStatus}
                displayEmpty
              >
                <MenuItem value="">
                  <em>All Statuses</em>
                </MenuItem>
                {STATUS_OPTIONS.map((s) => (
                  <MenuItem key={s.value} value={s.value}>
                    {s.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </>
        )}
      </Stack>
    </LocalizationProvider>
  )
}

export default EventsFilters
